import { useState } from 'react'
import { STATUS_COLOR, CATEGORY_COLOR } from '../../../shared/model.js'
import { HEAT_FREE, HEAT_BLOCKED } from '../../../shared/heat.js'
import { legendSwatch } from '../lib/mapStyle.js'
import { humanizeMinutes } from '../lib/i18n.js'
import { useFold } from '../lib/useFold.js'
import { IconClose } from './Icons.jsx'

/** What the colours mean, for whichever colouring the map shows right now. */
function legendModel({ t, colorMode, durationMin, heatScale }) {
  if (colorMode === 'category') {
    return {
      title: t('legendCategory'),
      items: ['visitor', 'mixed', 'resident'].map((c) => ({ color: CATEGORY_COLOR[c], label: t(`${c}Short`) })),
    }
  }
  if (colorMode === 'price' && durationMin && heatScale) {
    return {
      title: t('legendPrice', humanizeMinutes(durationMin, t)),
      items: [
        { color: HEAT_FREE, label: t('heatFree') },
        ...heatScale.legend.map((l) => ({
          color: l.color,
          label: l.cost != null ? t('price', l.cost) : t('priceRange', l.from, l.to),
        })),
        { color: HEAT_BLOCKED, label: t('heatBlockedShort') },
      ],
    }
  }
  return {
    title: t('legendNow'),
    items: [
      { color: STATUS_COLOR.free, label: t('legendFree') },
      { color: STATUS_COLOR.soon, label: t('legendSoon'), short: t('legendSoonShort') },
      { color: STATUS_COLOR.paid, label: t('legendPaid') },
    ],
  }
}

/**
 * Floating legend: a one-line strip of colour dots that expands to a labelled
 * card. Swatches render at the map's own fill alpha, so they match the map.
 * The strip's labels show for ten seconds whenever the legend appears or
 * changes (another stay, another language), then fold away to just the dots —
 * a traffic light — until a tap brings them back.
 */
export default function Legend({ t, theme, colorMode, durationMin, heatScale, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen)
  const { title, items } = legendModel({ t, colorMode, durationMin, heatScale })
  const [folded, show] = useFold(items.map((it) => it.short ?? it.label).join('|'), open)

  if (!open) {
    return (
      <button
        type="button"
        className={`legend legend--strip ${folded ? 'is-folded' : ''}`}
        aria-expanded="false"
        aria-label={`${t('showLegend')}: ${title}`}
        onClick={() => (folded ? show() : setOpen(true))}
      >
        {items.map((it, i) => (
          <span key={i} className="legend__pair">
            <span className="legend__dot" style={{ background: legendSwatch(it.color, theme) }} />
            <span className="legend__label">
              <span>{it.short ?? it.label}</span>
            </span>
          </span>
        ))}
      </button>
    )
  }
  return (
    <div className="legend legend--card">
      <div className="legend__head">
        <span className="legend__title">{title}</span>
        <button type="button" className="icon-btn icon-btn--sm" aria-label={t('hideLegend')} aria-expanded="true" onClick={() => setOpen(false)}>
          <IconClose />
        </button>
      </div>
      <ul className="legend__list">
        {items.map((it, i) => (
          <li key={i}>
            <span className="legend__swatch" style={{ background: legendSwatch(it.color, theme) }} />
            {it.label}
          </li>
        ))}
      </ul>
    </div>
  )
}
